/**
 * How a Data Lake ref (`hnsPath`) is signed — S8 Task 8.7, process-agnostic. A directory gets a directory SAS
 * (`sr=d` + `sdd`, NOTES S8 row S1); the file-system ROOT is not a directory SAS but S3's container SAS (`sr=c` — the
 * SDK signs `sr=c` for an empty path, row S3, ruling D7); a file signs as its blob. A stored access policy belongs to a
 * container, so a directory SAS has no policy mode (a directory SAS under a container policy is out of scope).
 */
import type { HnsPathRef, StorageOperationError, StorageResourceRef } from '../../types/azureStorage';
import { normalizeHnsPath } from './azureStorageHnsPaths';

export type HnsSasResourceKind = 'directory' | 'container' | 'blob';

export function hnsSasResourceKind(ref: HnsPathRef): HnsSasResourceKind {
  if (!ref.isDirectory) return 'blob';
  return ref.path === '' ? 'container' : 'directory';
}

/**
 * True for a Data Lake DIRECTORY below the root — the only ref a directory SAS (`sr=d`) is minted for. The predicate names
 * `isDirectory: true` so the false branch still carries `hnsPath` (the root and files are signed by the S3 paths).
 */
export const isDirectorySasTarget = (resource: StorageResourceRef): resource is HnsPathRef & { isDirectory: true } =>
  resource.kind === 'hnsPath' && hnsSasResourceKind(resource) === 'directory';

const invalid = (code: string, message: string): StorageOperationError => ({ category: 'invalid_input', code, retryable: false, message });

/** A path that is not a fixed point of `normalizeHnsPath` is refused before anything is signed (the name is in the signature). */
export function hnsSasPathProblem(ref: HnsPathRef): StorageOperationError | undefined {
  const normalized = normalizeHnsPath(ref.path, { allowRoot: ref.isDirectory });
  if (normalized.ok === false) return invalid('InvalidHnsPath', normalized.reason);
  return normalized.path === ref.path ? undefined : invalid('InvalidHnsPath', 'The Data Lake path must not start or end with "/".');
}

export const DIRECTORY_POLICY_REASON = 'Stored access policies belong to a container; a directory SAS cannot be generated under one. Generate an ad-hoc directory SAS instead.';

/** Row S2: a user-delegation SAS is capped by the signing identity's own Azure roles (a Contributor signer cannot grant `o` / `p`). */
export const SAS_USER_DELEGATION_SIGNER_LIMIT = 'A user-delegation SAS cannot grant more than the signing identity holds: permissions its Azure roles do not include (for example changing ownership or permissions without Storage Blob Data Owner) are refused when the token is used.';

/** The approval / dialog name of a Data Lake target. */
export function hnsSasTargetName(ref: HnsPathRef): string {
  const kind = hnsSasResourceKind(ref);
  if (kind === 'container') return `container ${ref.fileSystem} (file system root)`;
  return `${kind === 'directory' ? 'directory' : 'file'} ${ref.fileSystem}/${ref.path}`;
}
